import { inject, Service } from '@angular/core';
import { DialogService } from '@h5s/design-system';
import { FeatureLogService } from '@h5s/feature-log';
import { NotEmptyArray } from '@h5s/util-array';
import { ApiError, AppError, AppErrorBuilder, isApiErrorDto, Severity, TranslatableError } from '@h5s/util-error';
import { ExternalLoggerService } from '@h5s/util-logger';
import { formatString } from '@h5s/util-string';
import { TranslationService } from '@h5s/util-translate';
import { GraphQLFormattedError } from 'graphql/index';
import { take } from 'rxjs';
export const ERROR_DIALOG_TRANSLATIONS = {
  warningTitle: $localize`:@@common.dialog.title.attention:Attenzione`,
  closeButtonText: $localize`:@@common.button.close:Chiudi`,
};

@Service()
export class ErrorService {
  private readonly externalLoggerService = inject(ExternalLoggerService);
  private readonly dialogService = inject(DialogService);
  private readonly translationService = inject(TranslationService);
  private readonly featureLogService = inject(FeatureLogService);

  private readonly externalLogger = this.externalLoggerService.getLogger();

  /**
   * Send error to frontend error logging api adding payload to identify error circumstances
   * @param error error must be of BookingEngineException type
   */
  private sendErrorToRollbar(error: AppError): void {
    const payload = this.featureLogService.buildPayloadForExternalLogger(error);

    switch (error.severity) {
      case Severity.CRITICAL:
        return this.externalLogger.critical(error, payload);
      case Severity.WARNING:
        return this.externalLogger.warn(error, payload);
      case Severity.ERROR:
      default:
        return this.externalLogger.error(error, payload);
    }
  }

  public logError(error: AppError): void {
    // Log in console delle informazioni aggiuntive dell'errore
    if (error.info) {
      const errorInfo: [string, string | number | boolean][] = [];
      error.info.forEach((value, key) => errorInfo.push([key, value]));
      console.error(errorInfo);
    }

    /* Rollbar error logging
     * Per gli errori di tipo AppError mandiamo errore a Rollbar
     * ATTENZIONE: l'effettivo invio dell'errore dipende dalla configurazione per ambiente environment.rollbarConfig.enabled
     */
    this.sendErrorToRollbar(error);
  }

  /**
   * Gestisce un array di errori GraphQL e restituisce un'istanza di AppError.
   *
   * @param errors - Un array readonly di oggetti GraphQLError.
   * @returns AppError - Un'istanza di AppError costruita sulla base degli errori forniti.
   */
  public mapGraphQlFormattedErrorsToAppError(errors: NotEmptyArray<GraphQLFormattedError>): AppError {
    const causes: (ApiError | GraphQLFormattedError)[] = [];

    for (const error of errors) {
      if (error.extensions && isApiErrorDto(error.extensions)) {
        causes.push(new ApiError(error.extensions, error.message));
      } else {
        causes.push(error);
      }
    }

    /* Una richiesta GraphQL può avere come risultato un array di errori.
       Per semplificare la gestione:
       - Se abbiamo un errore singolo lo usiamo come causa dell'AppError
       - Se abbiamo più errori, usiamo come causa tutto l'array
    */
    const appError =
      causes.length === 1
        ? new AppErrorBuilder(causes[0].message).addCause(causes[0]).build()
        : new AppErrorBuilder(`${errors.length} errors in GraphQL response`).addCause(causes).build();

    return appError;
  }

  /**
   * Abbiamo uno o più errori di interazione utente. Li visualizziamo tradotti, in un dialog.
   */
  public translateAndDisplayErrors(translatableErrors: TranslatableError[]): void {
    if (translatableErrors.length === 0) return;

    const translatedErrorWithArguments = this.translateErrors(translatableErrors);
    return this.displayErrors(translatedErrorWithArguments);
  }

  /**
   * Effettua la traduzione delle chiavi passate.
   */
  public translateErrors(translatableErrors: TranslatableError[]): string {
    return translatableErrors
      .map(({ translationKey, arguments: args }) => {
        const translated = this.translationService.translateError(translationKey);
        return args ? formatString(translated, ...args) : translated;
      })
      .join('<br />');
  }

  /**
   * Visualizza la stringa di errore all'interno di una dialog.
   */
  public displayErrors(translatedErrorMessages: string): void {
    this.dialogService
      .showDanger({
        content: translatedErrorMessages,
        title: ERROR_DIALOG_TRANSLATIONS.warningTitle,
        cancelButtonText: undefined,
        confirmButtonText: ERROR_DIALOG_TRANSLATIONS.closeButtonText,
      })
      .pipe(take(1))
      .subscribe();
  }
}
